import type { ReactNode } from "react";

import { tilesAreConfigured, type Coordinate } from "../google-maps.ts";
import type { PositionConfidence } from "../ride-journey.ts";
import { useGoogleMap } from "./useGoogleMap.ts";

/**
 * The real places a screen knows about, where it knows any.
 *
 * Every field is optional and most screens supply none: a rider who has not chosen a destination
 * has no route, and a journey with `tripAbsence: not_joinable` has no vehicle to place. A screen
 * that supplies nothing gets the design's schematic, which is what it always got.
 */
export interface MapPlaces {
  readonly origin?: Coordinate;
  readonly destination?: Coordinate;
  readonly vehicle?: Coordinate;
}

/**
 * The three heroes `10 Rider App.dc.html` draws, built from the design's own geometry.
 *
 * All of it is CSS and markup, with no inline values at all: every colour, size and percentage lives
 * in a stylesheet where the acceptance check and the token test can both see it.
 *
 * The third is `5 Finding`'s radar, which the design writes as a React function called `finder`
 * rather than as data in its `SC` table — three pulsing rings, a conic sweep, four scattered cars
 * and a bobbing core. It was not built while the screen was not: DSP-04 published no `GET` for a
 * dispatch process, so `finding_candidates · try 1 of 3` and `Search time left 1:48` had no source,
 * and the illustration for an unbuildable screen is half of something. ADR 0031 served that read,
 * and `SearchHero` below is the design's `finder` with its values moved into `screens/search.css`.
 */

/** Which map a screen draws, which the design calls `d.map`. */
export type MapKind = "home" | "route" | "approach" | "trip";

/**
 * The schematic map: five layers and up to three pins.
 *
 * It **is** drawn, which is the opposite of the call `apps/driver-app` made, and the difference is
 * in the design files rather than in a judgement. `11 Driver App.dc.html` sets its `layers` key twice
 * in one object literal, so the second shorthand wins, every layer renders unstyled and invisible,
 * and its own approved reference screenshots show an empty hero. `10 Rider App.dc.html` has no such
 * duplicate: the layers carry their styles, and
 * `reference-screenshots/Mobile/Rider App/08 Driver coming.jpg` shows the grid, both roads, the
 * park, the dashed route and all three pins at all three widths. Both approved artefacts agree, so
 * the map is built.
 *
 * It is a schematic rather than a map of anywhere. There is no tile source on this platform, the
 * design draws none, and the coordinates EXP-002 publishes are not placed on it — the pins sit at
 * the design's own percentages. Drawing a real map would be adding something that is not in the
 * design; moving the pins by the driver's actual latitude would be drawing a position on a surface
 * that means nothing, which is worse than not drawing one.
 */
export function MapHero({
  kind,
  vehicle,
  accuracyMetres,
  staleness,
  places,
}: {
  readonly kind: MapKind;
  /**
   * Where this screen's places actually are, when it knows.
   *
   * **This is what changed on 24 October, and the header above it is the reason it took this
   * long.** That note said the pins sit at the design's percentages because *"there is no tile
   * source on this platform"* — true when it was written, and made false by ADR 0099's keys
   * without anyone going back to the decision. With a key and real coordinates the markers now
   * land where they are. Without either, every word of that note still holds.
   */
  readonly places?: MapPlaces;
  /**
   * How the vehicle marker may be drawn, or that there is none.
   *
   * Design rule 5: blue with a halo while on a job, dashed grey with an accuracy circle when the
   * position is stale, and **no marker at all** when there is none. The third is the common case
   * today, because a journey with `tripAbsence: not_joinable` has no tracking to read.
   */
  readonly vehicle?: PositionConfidence;
  /** TRP-02's own radius for the sample, which sizes the stale marker's accuracy circle. */
  readonly accuracyMetres?: number;
  /** How old a stale fix is, for the marker's title. Absent for a current one. */
  readonly staleness?: string;
}): ReactNode {
  const hasRoute = kind !== "home";
  const hasVehicle =
    (kind === "approach" || kind === "trip") && vehicle !== undefined && vehicle !== "absent";

  /*
   * The real places this screen knows, in the order the map should frame them.
   *
   * Everything below is conditional on there being at least one. A screen that has no coordinates
   * — and several have none, because the journey has not started — draws the design's schematic
   * exactly as it always did.
   */
  const points = [places?.origin, places?.destination, places?.vehicle].filter(
    (point): point is Coordinate => point !== undefined,
  );
  const { host, projection, refused } = useGoogleMap(points);
  const onTiles = projection !== undefined;

  /** A real place as the percentages the design's markers are positioned with. */
  const at = (point: Coordinate | undefined): { left: string; top: string } | undefined => {
    if (projection === undefined || point === undefined) return undefined;
    const placement = projection.place(point);
    return { left: `${String(placement.left)}%`, top: `${String(placement.top)}%` };
  };

  return (
    <div className={`map${onTiles ? " map--tiles" : ""}`} aria-hidden="true">
      {/*
       * The tile surface, under everything. Always rendered when a key is configured, because the
       * map has to have somewhere to mount before it can report a projection — `map--tiles` only
       * goes on once it has, which is what hides the schematic layers below.
       */}
      {tilesAreConfigured() && points.length > 0 && !refused ? (
        <div className="map__tiles" ref={host} />
      ) : null}

      <span className="map__grid" />
      <span className="map__road map__road--across" />
      <span className="map__road map__road--down" />
      <span className="map__park" />
      {hasRoute && !onTiles ? (
        /*
         * The design's dashed route, and only without tiles. Over a real map it would be a
         * straight line between two places that a vehicle will not travel in a straight line
         * between — a drawn claim about the road that nothing has checked. GEO-03 publishes a
         * real polyline; until it is plumbed through, two pins and no line is the honest render.
         */
        <span className={`map__route${kind === "approach" ? " map__route--approach" : ""}`} />
      ) : null}

      <span className="glyph map__pin map__pin--origin" style={at(places?.origin)}>
        trip_origin
      </span>
      {hasRoute ? (
        <span className="glyph map__pin map__pin--destination" style={at(places?.destination)}>
          location_on
        </span>
      ) : null}
      {hasVehicle ? (
        <span
          className={`glyph map__pin map__pin--vehicle${vehicle === "stale" ? " map__pin--stale" : ""}`}
          /*
           * `aria-hidden` is on the map as a whole, so this is the one place the position's own
           * confidence is readable rather than only visible. Design rule 5 draws the distinction —
           * a halo for a current fix, dashed grey with an accuracy circle for a stale one — and the
           * title says it in words for a reader who cannot see a dashed border.
           */
          title={staleness ?? undefined}
          style={
            /*
             * The design's two positions for this marker, and the accuracy circle's own diameter.
             *
             * `left: trip ? 55% : 20%`, `top: trip ? 32% : 52%` — the car is on the route during a
             * trip and near the pickup while approaching. It is a per-screen position of a shared
             * element, which is the one case a modifier class would be two classes for two numbers.
             *
             * The diameter is the only figure in this application written inline from data, because
             * it **is** data: it is the sample's own `accuracyMetres`, scaled, rather than a design
             * constant. A fixed circle would be an accuracy this application chose.
             */
            {
              /*
               * The real position where there is one and a map to put it on, the design's two
               * fixed positions otherwise. This is the whole difference tiles make to a marker:
               * where the percentages come from, not what is drawn.
               */
              ...(at(places?.vehicle) ??
                (kind === "trip" ? { left: "55%", top: "32%" } : { left: "20%", top: "52%" })),
              ...(accuracyMetres === undefined
                ? {}
                : { "--accuracy-diameter": `${String(accuracyCircle(accuracyMetres))}px` }),
            }
          }
        >
          local_taxi
        </span>
      ) : null}
    </div>
  );
}

/** Which illustration a screen draws, which the design calls `d.hero`. */
export type HeroKind = "done" | "none";

/**
 * `hero`: the radial wash and the 96dp ring, on the screens that draw no map.
 *
 * Three of the twenty-four use it. `done` is a green disc and a white tick, on `8 Complete` and
 * `8b Fare differs`; `none` is `5b No driver`'s grey `person_off` disc. `8 Complete` reaches `none`
 * as well, which is why it was built before the screen it belongs to was: a ride whose stage EXP-002
 * could not confirm must not draw a green tick, and the grey disc is the design's own vocabulary for
 * a thing that did not happen.
 */
export function Hero({ kind }: { readonly kind: HeroKind }): ReactNode {
  return (
    <div className="hero" aria-hidden="true">
      <span className={`hero__ring hero__ring--${kind}`}>
        <span className="glyph hero__glyph">{kind === "done" ? "check" : "person_off"}</span>
      </span>
    </div>
  );
}

/**
 * `finder`: `5 Finding`'s radar, which is the one hero the design writes as code rather than as data.
 *
 * The design's own function, element for element and in its order — three rings, the conic sweep,
 * four scattered cars, then the core with its filled `local_taxi`:
 *
 *     finder                    190×190, 34px below the centre of the wash
 *       ring ×3                 inset 0, a 2px brand circle, pulsing 0.8s apart
 *       sweep                   inset 8, a conic gradient turning once every 2.2s
 *       car ×4                  22dp discs at the design's four offsets
 *       core                    64dp, the action colour, bobbing 3px
 *
 * ## The one thing the design draws that this project's rules otherwise forbid
 *
 * `CLAUDE.md` section 1: _"Don't add anything that isn't in the design. No extra sections, cards,
 * stats, icons, animations…"_ — and immediately after it, _"Don't remove anything either."_ The
 * animation is **in** the design: `dcPulse`, `dcSweep` and `dcBob` are declared in its own
 * stylesheet and all three are used here and nowhere else in the file. So this is the one screen of
 * the twenty-four that moves, and `tests/stylesheet-tokens.test.ts` asserts that it is the only one.
 *
 * The design also honours `prefers-reduced-motion: reduce`, in its own `rm` check, and that is
 * copied rather than approximated: under the preference the rings stop with `opacity: .25` instead of
 * animating from `.9` to `0`, and the sweep and the core simply hold still. Which means the
 * acceptance check — whose browser context is `reducedMotion: "reduce"` — compares two static
 * pictures, and the comparison is of geometry rather than of whichever frame each side happened to
 * be on.
 *
 * `aria-hidden`, like both heroes above it: the search's progress is in the chip and the countdown
 * row, in words, and a spinning illustration announced to a screen reader would be noise over the
 * one place the state is actually said.
 */
export function SearchHero(): ReactNode {
  return (
    <div className="hero" aria-hidden="true">
      <div className="finder">
        {/*
         * Three rings and four cars as plain spans, with every number in `screens/search.css`.
         *
         * The rings differ only in their animation delay — the design's `ring(0)`, `ring(.8)`,
         * `ring(1.6)` — and the cars only in their offset from the centre, so each carries a
         * numbered modifier class rather than an inline value. That keeps this illustration's whole
         * geometry in one place the acceptance check and the token test can both read, which is the
         * claim this file's header makes about the other two heroes.
         */}
        <span className="finder__ring finder__ring--1" />
        <span className="finder__ring finder__ring--2" />
        <span className="finder__ring finder__ring--3" />
        <span className="finder__sweep" />
        <span className="glyph finder__car finder__car--1">local_taxi</span>
        <span className="glyph finder__car finder__car--2">local_taxi</span>
        <span className="glyph finder__car finder__car--3">local_taxi</span>
        <span className="glyph finder__car finder__car--4">local_taxi</span>
        <span className="finder__core">
          <span className="glyph finder__vehicle">local_taxi</span>
        </span>
      </div>
    </div>
  );
}

/**
 * How wide a stale fix's accuracy circle is on screen, from the radius TRP-02 published.
 *
 * The design's schematic map has no scale — it is a 26px grid, two roads and a park, not a map of
 * anywhere — so there is no metres-per-pixel to be faithful to. What the circle has to be is
 * **monotonic and bounded**: a 500-metre fix must draw a visibly larger circle than a 10-metre one,
 * and neither may swallow the hero. So it is the square root of the radius, scaled, clamped between
 * the marker's own 34px and 120px.
 *
 * Stated here with its reasoning rather than inline, because it is the one number on this screen
 * that is neither the design's nor the platform's, and somebody will want to know which.
 */
export function accuracyCircle(accuracyMetres: number): number {
  const scaled = 34 + Math.sqrt(Math.max(0, accuracyMetres)) * 6;
  return Math.round(Math.min(120, scaled));
}
